import type { ImportJob, ImportJobResult } from '../../types/admin';

export interface CsvJobCreateResponse {
  job: ImportJob;
  uploadUrl: string;
  uploadMethod: 'PUT';
  uploadContentType: string;
}

export interface ValidationIssue {
  row: number;
  message: string;
}

export interface ValidationSummary {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  issues: ValidationIssue[];
}

export interface ValidationPreview {
  summary: ValidationSummary;
  normalizedLinks: Array<{
    originalUrl: string;
    urlCode?: string;
    redirectType: '301' | '302';
  }>;
  canStart: boolean;
}

export interface ValidationPayloadResponse {
  validation: ValidationSummary;
  normalizedLinks?: Array<{
    originalUrl: string;
    urlCode?: string;
    redirectType: '301' | '302';
  }>;
  canStart?: boolean;
}

export interface ImportJobResultsResponse {
  results: ImportJobResult[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ImportJobsResponse {
  jobs: ImportJob[];
  nextCursor: string | null;
  hasMore: boolean;
}

export type ImportSourceMode = 'csv' | 'paste';
export type PasteFormat = 'csv' | 'json';
export type ComposerSubmissionMode = 'review' | 'start';

export const SAMPLE_CSV = [
  'urlcode,url,redirectType',
  ',https://example.com/landing,301',
  'promo-2026,https://example.com/promo,302',
  ',https://example.com/newsletter,301',
].join('\n');

export const JOBS_PER_PAGE = 8;
export const RESULTS_PER_PAGE = 25;

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 KB';
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function progressLabel(job: ImportJob): string {
  if (job.totalRows <= 0) {
    return '0%';
  }

  return `${job.progress}%`;
}

export function parseApiErrorPayload(payload: unknown, fallbackMessage: string): Error {
  if (payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string') {
    return new Error(payload.error);
  }

  return new Error(fallbackMessage);
}

export function jobStatusBadgeVariant(status: ImportJob['status']): 'success' | 'destructive' | 'default' | 'secondary' | 'warning' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'cancelled':
      return 'secondary';
    case 'running':
      return 'default';
    case 'review':
      return 'secondary';
    case 'paused':
      return 'warning';
    default:
      return 'secondary';
  }
}

export function resultStatusBadgeVariant(status: ImportJobResult['status']): 'success' | 'destructive' | 'default' | 'secondary' | 'warning' {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'invalid':
      return 'warning';
    default:
      return 'secondary';
  }
}

export function isValidationBusy(job: Pick<ImportJob, 'sourceType' | 'validationState'> | null | undefined): boolean {
  return Boolean(
    job
    && job.sourceType === 'csv'
    && (job.validationState === 'queued' || job.validationState === 'running'),
  );
}

/** Returns the i18n key for the validation status badge label. */
export function validationLabelKey(job: Pick<ImportJob, 'sourceType' | 'validationState' | 'validationInvalidRows'> | null | undefined): string {
  if (!job || job.sourceType !== 'csv') {
    return 'imports.detail.validationLabelNA';
  }

  switch (job.validationState) {
    case 'queued':
      return 'imports.detail.validationLabelQueued';
    case 'running':
      return 'imports.detail.validationLabelRunning';
    case 'completed':
      return (job.validationInvalidRows || 0) > 0
        ? 'imports.detail.validationLabelCompletedWithIssues'
        : 'imports.detail.validationLabelCompleted';
    case 'failed':
      return 'imports.detail.validationLabelFailed';
    default:
      return 'imports.detail.validationLabelNotStarted';
  }
}

/** Returns the i18n key for the validation description copy. */
export function validationCopyKey(job: ImportJob | null | undefined): string {
  if (!job || job.sourceType !== 'csv') {
    return 'imports.detail.validationCopyDefault';
  }

  switch (job.validationState) {
    case 'queued':
      return 'imports.detail.validationCopyQueued';
    case 'running':
      return 'imports.detail.validationCopyRunning';
    case 'completed':
      return (job.validationInvalidRows || 0) > 0
        ? 'imports.detail.validationCopyCompletedWithIssues'
        : 'imports.detail.validationCopyCompleted';
    case 'failed':
      return 'imports.detail.validationCopyFailed';
    default:
      return 'imports.detail.validationCopyDefault';
  }
}

export function validationTone(job: Pick<ImportJob, 'sourceType' | 'validationState' | 'validationInvalidRows'> | null | undefined): 'idle' | 'ready' | 'issues' {
  if (!job || job.sourceType !== 'csv') {
    return 'idle';
  }

  if (job.validationState === 'failed' || (job.validationInvalidRows || 0) > 0) {
    return 'issues';
  }

  if (job.validationState === 'queued' || job.validationState === 'running' || job.validationState === 'completed') {
    return 'ready';
  }

  return 'idle';
}

export function validationBadgeVariant(tone: 'idle' | 'ready' | 'issues'): 'secondary' | 'success' | 'destructive' {
  switch (tone) {
    case 'ready':
      return 'success';
    case 'issues':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/** Returns the i18n key for the empty results message. */
export function emptyResultsKey(job: ImportJob): string {
  if (job.sourceType === 'csv' && isValidationBusy(job)) {
    return 'imports.detail.emptyValidationRunning';
  }

  if (job.status === 'review') {
    return job.sourceType === 'csv'
      ? 'imports.detail.emptyReviewCsv'
      : 'imports.detail.emptyReviewApi';
  }

  if (job.status === 'running') {
    return 'imports.detail.emptyRunning';
  }

  if (job.status === 'cancelled') {
    return 'imports.detail.emptyCancelled';
  }

  return 'imports.detail.emptyDefault';
}
